import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { BaseDeckPane } from './BaseDeckPane';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import {
    deriveArchetypeDisclosureChips,
    deriveArchetypeDriverChips,
    deriveArchetypeHeadline,
    deriveArchetypeHybridChips,
    deriveArchetypeTuningChips,
} from '../../../data/deckArchetypeProfileMarkers';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { ArchetypeProfilePanel } from './ArchetypeProfilePanel';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';
import './archetypesPane.css';
// Reuse the collapsible-section, facet, and chip families defined by the
// other deck panes so the archetypes panel reads as the same family.
import './PowerLevelPane.css';
import './CombosPane.css';

const ARCHETYPE_ORDER = ['MIDRANGE', 'CONTROL', 'COMBO'];

// Derive a hero profile from the archetype percentages map. Dominant decks
// (one archetype >= 65%) get the `dominant` tier and the title glow;
// focused decks (45–65%) read neutral; balanced decks drop into a muted
// tier. Top sub-archetype adds flavor to the dominant title so the header
// doesn't just repeat the parent label.
function archetypesHeroProfile(percentages) {
    const safe = percentages || {};
    const rows = ARCHETYPE_ORDER.map((key) => ({
        key,
        pct: Number(safe?.[key]?.percentage || 0),
        subArchetypes: safe?.[key]?.subArchetypes || {},
    })).sort((a, b) => b.pct - a.pct);

    const primary = rows[0];
    if (!primary || primary.pct === 0) {
        return {
            name: 'Archetype Mix',
            tier: 'balanced',
            hint: 'No archetype signal detected for this deck.',
            primary: null,
            rows,
        };
    }

    const primaryLabel = getPrettyLabel(primary.key);
    const topSubEntry = Object.entries(primary.subArchetypes)
        .map(([key, val]) => ({ key, pct: Number(val?.percentage || 0) }))
        .sort((a, b) => b.pct - a.pct)[0];

    let tier = 'balanced';
    let name = 'Balanced Mix';
    if (primary.pct >= 65) {
        tier = 'dominant';
        name =
            topSubEntry && topSubEntry.pct > 0
                ? `${primaryLabel} · ${getPrettyLabel(topSubEntry.key)}`
                : `${primaryLabel}-Dominant`;
    } else if (primary.pct >= 45) {
        tier = 'focused';
        name = `${primaryLabel}-Leaning`;
    }

    const hint = rows
        .filter((r) => r.pct > 0)
        .map((r) => `${Math.round(r.pct)}% ${getPrettyLabel(r.key)}`)
        .join(' · ');

    return { name, tier, hint, primary, rows };
}

export function ArchetypesPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);
    const openCardInfo = useOpenCardInfo();

    const profile = deck?.details?.archetypes?.profile || null;
    const heroProfile = archetypesHeroProfile(deck?.details?.archetypes?.percentages);
    const primaryLabel = heroProfile.primary ? getPrettyLabel(heroProfile.primary.key) : '—';

    const [profilePanelOpen, setProfilePanelOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    const headline = useMemo(() => deriveArchetypeHeadline(profile), [profile]);
    const drivers = useMemo(() => deriveArchetypeDriverChips(profile), [profile]);
    const hybridSignals = useMemo(() => deriveArchetypeHybridChips(profile), [profile]);
    const disclosures = useMemo(() => deriveArchetypeDisclosureChips(profile), [profile]);
    const tuning = useMemo(() => deriveArchetypeTuningChips(profile), [profile]);

    const handleSelectCard = (card) => {
        if (card) openCardInfo(card);
    };

    const hasProfileContent =
        !!profile?.headline?.primary &&
        (headline ||
            drivers.length > 0 ||
            hybridSignals.length > 0 ||
            disclosures.length > 0 ||
            tuning.length > 0 ||
            (profile?.majors || []).some((m) => (m.share || 0) > 0) ||
            (profile?.markers || []).length > 0 ||
            !!profile?.kindred?.top);

    const categories = useMemo(() => buildCategories(deck, cards), [deck, cards]);

    return (
        <BaseDeckPane title={'Archetypes'} fieldName="name" content={<div />} showScore={false} categories={categories}>
            <HeroPanel
                tone="magenta"
                className="archetypesHero"
                data-tier={heroProfile.tier}
                eyebrow="Archetype Mix"
                title={heroProfile.name}
                hint={heroProfile.hint}
                aside={
                    <>
                        <HeroPanelTotal
                            label={primaryLabel}
                            value={heroProfile.primary ? Math.round(heroProfile.primary.pct) : '—'}
                            sub="% primary"
                        />
                        {heroProfile.rows.slice(1).filter((r) => r.pct > 0).length > 0 && (
                            <div className="archetypesHeroTags">
                                {heroProfile.rows
                                    .slice(1)
                                    .filter((r) => r.pct > 0)
                                    .map((r) => (
                                        <span
                                            key={r.key}
                                            className="archetypesHeroTag"
                                            title={`${getPrettyLabel(r.key)} · ${r.pct.toFixed(1)}%`}
                                        >
                                            <span className="archetypesHeroTagLabel">{getPrettyLabel(r.key)}</span>
                                            <span className="archetypesHeroTagValue">{Math.round(r.pct)}%</span>
                                        </span>
                                    ))}
                            </div>
                        )}
                    </>
                }
            />


            {hasProfileContent && (
                <section className={`powerSection powerSection--collapsible${profilePanelOpen ? ' is-open' : ''}`}>
                    <button
                        type="button"
                        className="powerSectionToggle"
                        aria-expanded={profilePanelOpen}
                        aria-controls="archetypesProfilePanelBody"
                        onClick={() => setProfilePanelOpen((v) => !v)}
                    >
                        <span className="powerSectionEyebrow">
                            <span className="powerSectionChevron" aria-hidden="true">
                                {profilePanelOpen ? '▾' : '▸'}
                            </span>
                            Archetype profile
                        </span>
                        <span className="powerSectionHint">
                            Why this identity won, the sub-archetypes carrying the deck, what to disclose pre-game, and
                            where to lean if you want to tune it.
                        </span>
                    </button>
                    {profilePanelOpen && (
                        <div id="archetypesProfilePanelBody" className="powerSectionBody">
                            <ArchetypeProfilePanel
                                profile={profile}
                                headline={headline}
                                drivers={drivers}
                                hybridSignals={hybridSignals}
                                disclosures={disclosures}
                                tuning={tuning}
                                cards={cards}
                                onSelectCard={handleSelectCard}
                            />
                        </div>
                    )}
                </section>
            )}
        </BaseDeckPane>
    );
}

// Categories drive BaseDeckPane's bottom card-stat browser. Build from the
// raw archetype tree (not the profile shape) so cached decks pre-cache-bump
// still show the per-archetype card lists.
function buildCategories(deck, cards) {
    const archetypes = deck?.details?.archetypes?.archetypes;
    if (!archetypes) return {};
    const list = {};

    Object.keys(archetypes).forEach((parentArchetype) => {
        if (parentArchetype === 'MISC') return;
        const item = archetypes[parentArchetype];
        const subArchetypeList = {};
        let items = [];
        const label = getPrettyLabel(parentArchetype);

        Object.keys(item?.subArchetypes || {}).forEach((subArchetype) => {
            const subLabel = getPrettyLabel(subArchetype);
            const subItem = item.subArchetypes[subArchetype];

            subArchetypeList[subArchetype] = {
                label: subLabel,
                id: subArchetype,
                count: subItem.list.length,
                list: subItem.list.map((_key) => ({ ...cards?.[_key] })),
                parentCategory: parentArchetype,
            };

            items = items.concat(subItem.list.map((_key) => ({ ...cards?.[_key], id: _key })) || []);
        });

        const itemsHash = {};
        items.forEach((entry) => {
            itemsHash[entry.id] = entry;
        });
        items = Object.keys(itemsHash).map((key) => itemsHash[key]);

        list[parentArchetype] = {
            label,
            id: parentArchetype,
            count: items?.length || 0,
            list: items,
            color: '#FFFFFF',
            subCategories: subArchetypeList,
        };
    });

    Object.keys(list).forEach((key) => {
        if ((list?.[key]?.list?.length || 0) < 1) {
            delete list[key];
            return;
        }
        Object.keys(list[key].subCategories).forEach((subKey) => {
            if ((list[key].subCategories[subKey]?.list?.length || 0) < 1) {
                delete list[key].subCategories[subKey];
            }
        });
    });

    return list;
}
